/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { RoomContext, useLocalParticipant } from "@livekit/components-react";
import {
  Button as CpdButton,
  IconButton,
  Text,
  Tooltip,
} from "@vector-im/compound-web";
import {
  ConnectionState,
  DisconnectReason,
  type Room as LivekitRoom,
  RoomEvent as LivekitRoomEvent,
} from "livekit-client";
import { type MatrixClient, type Room as MatrixRoom } from "matrix-js-sdk";
import {
  type FC,
  type PointerEvent,
  type TouchEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from "react";
import useMeasure from "react-use-measure";
import { type MatrixRTCSession } from "matrix-js-sdk/lib/matrixrtc";
import classNames from "classnames";
import { BehaviorSubject, map } from "rxjs";
import {
  useObservable,
  useObservableEagerState,
  useSubscription,
} from "observable-hooks";
import { logger } from "matrix-js-sdk/lib/logger";
import { RoomAndToDeviceEvents } from "matrix-js-sdk/lib/matrixrtc/RoomAndToDeviceKeyTransport";
import {
  VoiceCallSolidIcon,
  VolumeOnSolidIcon,
} from "@vector-im/compound-design-tokens/assets/web/icons";
import { useTranslation } from "react-i18next";

import { useMeetingAutomation } from "./useMeetingAutomation";
import LogoMark from "../icons/LogoMark.svg?react";
import type { IWidgetApiRequest } from "matrix-widget-api";
import {
  EndCallButton,
  MicButton,
  VideoButton,
  RaiseHandButton,
  ShareScreenButton,
  MoreOptionsMenuButton,
  ReactionToggleButton,
  ShareLinkButton,
} from "../button";
import { Header, LeftNav, RightNav, RoomHeaderInfo } from "../Header";
import { AgendaProvider } from "./AgendaContext";
import { AgendaStrip } from "./AgendaStrip";
import { type HeaderStyle, useUrlParams } from "../UrlParams";
import { useCallViewKeyboardShortcuts } from "../useCallViewKeyboardShortcuts";
import { ElementWidgetActions, widget } from "../widget";
import styles from "./InCallView.module.css";
import { GridTile } from "../tile/GridTile";
import { type OTelGroupCallMembership } from "../otel/OTelGroupCallMembership";
import { SettingsModal, defaultSettingsTab } from "../settings/SettingsModal";
import { useRageshakeRequestModal } from "../settings/submit-rageshake";
import { RageshakeRequestModal } from "./RageshakeRequestModal";
import { SCREEN_SHARE_PUBLISH_OPTIONS } from "../livekit/options";
import { useLivekit } from "../livekit/useLivekit.ts";
import { useWakeLock } from "../useWakeLock";
import { useMergedRefs } from "../useMergedRefs";
import { type MuteStates } from "./MuteStates";
import { type MatrixInfo } from "./VideoPreview";
import { InviteButton } from "../button/InviteButton";
import { LayoutToggle } from "./LayoutToggle";
import { useOpenIDSFU } from "../livekit/openIDSFU";
import {
  CallViewModel,
  type GridMode,
  type Layout,
} from "../state/CallViewModel";
import { Grid, type TileProps } from "../grid/Grid";
import { useInitial } from "../useInitial";
import { SpotlightTile } from "../tile/SpotlightTile";
import {
  useRoomEncryptionSystem,
  type EncryptionSystem,
} from "../e2ee/roomEncryption";
import { E2eeType } from "../e2ee/e2eeType";
import { makeGridLayout } from "../grid/GridLayout";
import {
  type CallLayoutOutputs,
  defaultPipAlignment,
  defaultSpotlightAlignment,
} from "../grid/CallLayout";
import { makeOneOnOneLayout } from "../grid/OneOnOneLayout";
import { makeSpotlightExpandedLayout } from "../grid/SpotlightExpandedLayout";
import { makeSpotlightLandscapeLayout } from "../grid/SpotlightLandscapeLayout";
import { makeSpotlightPortraitLayout } from "../grid/SpotlightPortraitLayout";
import { GridTileViewModel, type TileViewModel } from "../state/TileViewModel";
import {
  ReactionsSenderProvider,
  useReactionsSender,
} from "../reactions/useReactionsSender";
import { ReactionsAudioRenderer } from "./ReactionAudioRenderer";
import { ReactionsOverlay } from "./ReactionsOverlay";
import { RaisedHandsOverlay } from "./RaisedHandsOverlay";
import {
  CallEventAudioRenderer,
  type CallEventSounds,
} from "./CallEventAudioRenderer";
import {
  debugTileLayout as debugTileLayoutSetting,
  useExperimentalToDeviceTransport as useExperimentalToDeviceTransportSetting,
  useNewMembershipManager as useNewMembershipManagerSetting,
  developerMode as developerModeSetting,
  useSetting,
} from "../settings/settings";
import { ReactionsReader } from "../reactions/ReactionsReader";
import { useTypedEventEmitter } from "../useEvents.ts";
import { MatrixAudioRenderer } from "../livekit/MatrixAudioRenderer.tsx";
import { muteAllAudio$ } from "../state/MuteAllAudioModel.ts";
import { MeetingModeration } from "./MeetingModeration";
import { useMatrixRTCSessionMemberships } from "../useMatrixRTCSessionMemberships.ts";
import { useMediaDevices } from "../MediaDevicesContext.ts";
import { EarpieceOverlay } from "./EarpieceOverlay.tsx";
import { useAppBarHidden, useAppBarSecondaryButton } from "../AppBar.tsx";
import { useBehavior } from "../useBehavior.ts";
import { Toast } from "../Toast.tsx";
import overlayStyles from "../Overlay.module.css";
import { Avatar, Size as AvatarSize } from "../Avatar";
import waitingStyles from "./WaitingForJoin.module.css";
import { prefetchSounds } from "../soundUtils";
import { useAudioContext } from "../useAudioContext";
import ringtoneMp3 from "../sound/ringtone.mp3?url";
import ringtoneOgg from "../sound/ringtone.ogg?url";
import { PRODUCT_NAME } from "../branding";
import { useCallElapsedTimer } from "./useCallElapsedTimer";
import { useCallStartTime } from "./useCallStartTime";
import { useLivekitRoomSid } from "./useLivekitRoomSid";
import { useCallRecording } from "../callrecorder";
import {
  ProtocolistRecipientsModal,
  useProtocolistRecording,
} from "../protocolist";
import { useRecordingNotice } from "./useRecordingNotice";
import { type CallAutoRageshakeApi } from "./useCallAutoRageshake.ts";
import {
  callTraceJournal,
  describeTraceError,
  getRuntimeSnapshot,
} from "../telemetry";
import { type LivekitIncidentPayload } from "../telemetry/types";
import {
  CallRecoverySignalRelay,
  type ActiveCallRecoveryResult,
  useActiveCallRecovery,
} from "../recovery/useActiveCallRecovery";
import type { TerminalCallError } from "../recovery/CallRecoveryTypes";
import type { CallRecoveryCoordinator } from "../recovery/CallRecoveryCoordinator";
import type { CallPresentationState } from "../recovery/CallRecoveryAdapters";
import { constant, type Behavior } from "../state/Behavior";

const canScreenshare = "getDisplayMedia" in (navigator.mediaDevices ?? {});

const maxTapDurationMs = 400;
/** Не снимаем reconnect UI по краткому оптимистичному network/SDK статусу. */
export const RECONNECT_STABILITY_DELAY_MS = 3_000;

/**
 * Сколько держать на экране исход вызова, прежде чем выйти из звонка.
 * Это единственное окно, в котором звонящий узнаёт, отклонили его или не взяли
 * трубку: дальше звонилка закрывается и сказать ему уже негде.
 */
export const CALL_OUTCOME_NOTICE_MS = 2_500;
const noopAutoRageshake: CallAutoRageshakeApi = {
  reportIncident: () => {},
  reportError: () => {},
  recordReconnect: () => {},
};
const noHardRecovery = (): boolean => false;
const noPresentationState$ = constant<CallPresentationState>({
  cycleId: null,
  recoveryActive: false,
  frozen: false,
  phase: "idle",
  screenShareStoppedCycleId: null,
});

/** Собирает ограниченный снимок публичного состояния LiveKit без SDP и IP. */
function getLivekitIncidentSnapshot(
  room: LivekitRoom,
  rtcSession: MatrixRTCSession,
): LivekitIncidentPayload {
  const localParticipant = room.localParticipant;
  const memberships = rtcSession.memberships;
  const matrixClient = rtcSession.room.client;
  const syncError = matrixClient.getSyncStateData()?.error;
  return {
    roomState: room.state,
    localParticipantSid: localParticipant.sid || null,
    localIdentity: localParticipant.identity || null,
    localConnectionQuality: localParticipant.connectionQuality,
    localTrackCount: localParticipant.trackPublications.size,
    microphoneEnabled: localParticipant.isMicrophoneEnabled,
    cameraEnabled: localParticipant.isCameraEnabled,
    screenShareEnabled: localParticipant.isScreenShareEnabled,
    remoteParticipantCount: room.remoteParticipants.size,
    activeSpeakerCount: room.activeSpeakers.length,
    matrixMembershipCount: memberships.length,
    matrixParticipantCount: new Set(
      memberships.map((membership) => membership.sender).filter(Boolean),
    ).size,
    ownMatrixMembershipPresent: memberships.some(
      (membership) =>
        membership.sender === rtcSession.room.client.getUserId() &&
        membership.deviceId === rtcSession.room.client.getDeviceId(),
    ),
    membershipManagerStatus: rtcSession.membershipStatus ?? null,
    matrixSyncState: matrixClient.getSyncState(),
    matrixSyncError: syncError ? describeTraceError(syncError) : null,
    activeFocusType: rtcSession.getActiveFocus()?.type ?? null,
    e2eeEnabled: room.isE2EEEnabled,
    livekitServerVersion: room.serverInfo?.version ?? null,
    livekitServerRegion: room.serverInfo?.region ?? null,
    runtime: getRuntimeSnapshot(),
  };
}

export interface ActiveCallProps
  extends Omit<InCallViewProps, "vm" | "livekitRoom" | "connState"> {
  e2eeSystem: EncryptionSystem;
  /** Уникальный идентификатор одной пользовательской session Join→Leave. */
  callSessionId: string;
  onRecoveryTerminal?: (error: TerminalCallError) => void;
  registerRecoveryCancel?: (
    cancel: ActiveCallRecoveryResult["cancel"],
  ) => () => void;
  /** Ошибка исчерпанной стандартной MatrixRTC entry attempt. */
  standardConnectionFailure?: unknown;
}

export const ActiveCall: FC<ActiveCallProps> = (props) => {
  const mediaDevices = useMediaDevices();
  const {
    autoLeaveWhenOthersLeft,
    sendNotificationType,
    waitForCallPickup,
    callIntent,
  } = useUrlParams();
  const recoverySignalRelay = useInitial(() => new CallRecoverySignalRelay());
  const deferredRoomTeardown = useInitial(
    (): { timer: ReturnType<typeof setTimeout> | null } => ({ timer: null }),
  );
  const recoveryCoordinatorRef = useRef<CallRecoveryCoordinator | null>(null);
  const standardConnectionEstablishedRef = useRef(false);
  const reportedStandardConnectionFailureRef = useRef<unknown>(null);
  const recoveryDelegate = useInitial(() => ({
    isHardRecoveryActive: (): boolean => {
      const phase = recoveryCoordinatorRef.current?.state$.value.phase;
      return (
        phase === "recovering" ||
        phase === "waiting_for_network" ||
        phase === "verifying"
      );
    },
    getHardRecoveryPhase: recoverySignalRelay.getRecoveryPhase,
    subscribeHardRecoveryPhase: recoverySignalRelay.subscribeRecoveryPhase,
    onInitialConnectionFailed: (error: unknown): boolean =>
      recoverySignalRelay.emit({ type: "initial_connection_failed", error }),
    onFocusSwitchFailed: (error: unknown): void => {
      recoverySignalRelay.emit({ type: "focus_switch_failed", error });
    },
  }));
  const onCredentialsError = useCallback(
    (error: unknown): boolean =>
      recoverySignalRelay.emit({
        type: standardConnectionEstablishedRef.current
          ? "focus_switch_failed"
          : "initial_connection_failed",
        error,
      }),
    [recoverySignalRelay],
  );
  const sfuConfig = useOpenIDSFU(
    props.client,
    props.rtcSession,
    onCredentialsError,
  );
  const { livekitRoom, connState } = useLivekit(
    props.rtcSession,
    props.muteStates,
    sfuConfig,
    props.e2eeSystem,
    recoveryDelegate,
  );
  if (connState === ConnectionState.Connected) {
    standardConnectionEstablishedRef.current = true;
  }
  const [useNewMembershipManager] = useSetting(useNewMembershipManagerSetting);
  const [useExperimentalToDeviceTransport] = useSetting(
    useExperimentalToDeviceTransportSetting,
  );
  const recovery = useActiveCallRecovery({
    callSessionId: props.callSessionId,
    client: props.client,
    matrixRoom: props.matrixRoom,
    rtcSession: props.rtcSession,
    livekitRoom,
    muteStates: props.muteStates,
    mediaDevices,
    encryptMedia: props.e2eeSystem.kind === E2eeType.PER_PARTICIPANT,
    useNewMembershipManager,
    useExperimentalToDeviceTransport,
    notificationType: sendNotificationType,
    callIntent,
    relay: recoverySignalRelay,
    onTerminal:
      props.onRecoveryTerminal ??
      ((error): void =>
        logger.error("Hard recovery завершился terminal", error)),
    autoRageshake: props.autoRageshake ?? noopAutoRageshake,
  });
  recoveryCoordinatorRef.current = recovery.coordinator;
  useEffect(() => {
    if (
      props.standardConnectionFailure == null ||
      reportedStandardConnectionFailureRef.current ===
        props.standardConnectionFailure
    ) {
      return;
    }
    reportedStandardConnectionFailureRef.current =
      props.standardConnectionFailure;
    recoverySignalRelay.emit({
      type: "initial_connection_failed",
      error: props.standardConnectionFailure,
    });
  }, [props.standardConnectionFailure, recoverySignalRelay]);
  const registerRecoveryCancel = props.registerRecoveryCancel;
  useEffect(
    () => registerRecoveryCancel?.(recovery.cancel),
    [registerRecoveryCancel, recovery.cancel],
  );
  const connStateObservable$ = useObservable(
    (inputs$) => inputs$.pipe(map(([connState]) => connState)),
    [connState],
  );
  const [vm, setVm] = useState<CallViewModel | null>(null);

  useEffect(() => {
    if (deferredRoomTeardown.timer !== null) {
      clearTimeout(deferredRoomTeardown.timer);
      deferredRoomTeardown.timer = null;
    }
    logger.info(
      `[Lifecycle] InCallView Component mounted, livekit room state ${livekitRoom?.state}`,
    );
    return (): void => {
      logger.info(
        `[Lifecycle] InCallView Component unmounted, livekit room state ${livekitRoom?.state}`,
      );
      // StrictMode cleanup/setup не является завершением call session.
      deferredRoomTeardown.timer = setTimeout(() => {
        deferredRoomTeardown.timer = null;
        livekitRoom
          ?.disconnect()
          .then(() => {
            logger.info(
              `[Lifecycle] Disconnected from livekit room, state:${livekitRoom?.state}`,
            );
          })
          .catch((e) => {
            logger.error(
              "[Lifecycle] Failed to disconnect from livekit room",
              e,
            );
          });
      }, 0);
    };
  }, [deferredRoomTeardown, livekitRoom]);

  useEffect(() => {
    if (livekitRoom !== undefined) {
      const reactionsReader = new ReactionsReader(props.rtcSession);
      const vm = new CallViewModel(
        props.rtcSession,
        props.matrixRoom,
        livekitRoom,
        mediaDevices,
        {
          encryptionSystem: props.e2eeSystem,
          autoLeaveWhenOthersLeft,
          waitForCallPickup:
            waitForCallPickup && sendNotificationType === "ring",
          roomMembersHydrator: recovery.roomMembersHydrator,
          rtcMembersHydrator: recovery.rtcMembersHydrator,
          recoveryActive$: recovery.presentation.recoveryActive$,
        },
        connStateObservable$,
        reactionsReader.raisedHands$,
        reactionsReader.reactions$,
      );
      setVm(vm);
      return (): void => {
        vm.destroy();
        reactionsReader.destroy();
      };
    }
  }, [
    props.rtcSession,
    props.matrixRoom,
    livekitRoom,
    mediaDevices,
    props.e2eeSystem,
    connStateObservable$,
    autoLeaveWhenOthersLeft,
    sendNotificationType,
    waitForCallPickup,
    recovery.roomMembersHydrator,
    recovery.rtcMembersHydrator,
    recovery.presentation,
  ]);

  if (vm === null) return null;

  const onLeave = (cause: "user", soundFile?: CallEventSounds): void => {
    recovery.cancel("user_leave");
    props.onLeave(cause, soundFile);
  };

  return (
    <RoomContext value={livekitRoom}>
      <ReactionsSenderProvider vm={vm} rtcSession={props.rtcSession}>
        <InCallView
          {...props}
          onLeave={onLeave}
          isHardRecoveryActive={recoveryDelegate.isHardRecoveryActive}
          presentationState$={recovery.presentationState$}
          vm={vm}
          livekitRoom={livekitRoom}
        />
      </ReactionsSenderProvider>
    </RoomContext>
  );
};

export interface InCallViewProps {
  client: MatrixClient;
  vm: CallViewModel;
  matrixInfo: MatrixInfo;
  rtcSession: MatrixRTCSession;
  matrixRoom: MatrixRoom;
  livekitRoom: LivekitRoom;
  muteStates: MuteStates;
  /** Function to call when the user explicitly ends the call */
  onLeave: (cause: "user", soundFile?: CallEventSounds) => void;
  header: HeaderStyle;
  otelGroupCallMembership?: OTelGroupCallMembership;
  onShareClick: (() => void) | null;
  autoRageshake?: CallAutoRageshakeApi;
  /** Синхронная проверка нужна event handler-ам без React render race. */
  isHardRecoveryActive?: () => boolean;
  /** Readonly lifecycle единственного presentation store call session. */
  presentationState$?: Behavior<CallPresentationState>;
}

export const InCallView: FC<InCallViewProps> = ({
  client,
  vm,
  matrixInfo,
  rtcSession,
  matrixRoom,
  livekitRoom,
  muteStates,
  onLeave,
  header: headerStyle,
  onShareClick,
  autoRageshake = noopAutoRageshake,
  isHardRecoveryActive = noHardRecovery,
  presentationState$ = noPresentationState$,
}) => {
  const { t } = useTranslation();
  const { supportsReactions, sendReaction, toggleRaisedHand } =
    useReactionsSender();

  useWakeLock();
  const connectionState = useObservableEagerState(vm.livekitConnectionState$);
  const callId = useLivekitRoomSid({ livekitRoom, connectionState });
  const memberships = useMatrixRTCSessionMemberships(rtcSession);
  const callStartTime = useCallStartTime({
    client,
    rtcSession,
    callId,
    connectionState,
  });
  const {
    displayValue: callElapsedTimer,
    isVisible: isCallElapsedTimerVisible,
  } = useCallElapsedTimer({
    startMs: callStartTime.startMs,
    isVisible: callStartTime.isVisible,
  });
  const participantCount = useBehavior(vm.participantCount$);
  const reconnecting = useBehavior(vm.reconnecting$);
  const presentationState = useBehavior(presentationState$);
  const hardRecoveryActive = presentationState.recoveryActive;
  const [browserOffline, setBrowserOffline] = useState(() => !navigator.onLine);
  useEffect(() => {
    const onOffline = (): void => setBrowserOffline(true);
    const onOnline = (): void => setBrowserOffline(false);
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    return (): void => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  }, []);
  const reconnectSignalActive =
    browserOffline || reconnecting || hardRecoveryActive;
  const [showReconnectToast, setShowReconnectToast] = useState(
    reconnectSignalActive,
  );
  useEffect(() => {
    if (reconnectSignalActive) {
      setShowReconnectToast(true);
      return;
    }
    // navigator.onLine становится true уже при подключении к роутеру. Даём
    // Matrix/LiveKit время опровергнуть этот оптимистичный browser status.
    const timeoutId = window.setTimeout(
      () => setShowReconnectToast(false),
      RECONNECT_STABILITY_DELAY_MS,
    );
    return (): void => window.clearTimeout(timeoutId);
  }, [reconnectSignalActive]);
  const [screenShareNoticeCycleId, setScreenShareNoticeCycleId] = useState<
    string | null
  >(null);
  useEffect(() => {
    if (
      presentationState.phase === "connected" &&
      presentationState.screenShareStoppedCycleId !== null
    ) {
      setScreenShareNoticeCycleId(presentationState.screenShareStoppedCycleId);
    } else if (
      presentationState.phase === "recovering" ||
      presentationState.phase === "cancelled" ||
      presentationState.phase === "terminal"
    ) {
      setScreenShareNoticeCycleId(null);
    }
  }, [presentationState.phase, presentationState.screenShareStoppedCycleId]);
  const previousReconnectingRef = useRef(reconnecting);
  const intentionalLeaveRef = useRef(false);
  const joinedTracedRef = useRef(false);
  useEffect(() => {
    if (callId) callTraceJournal.resolveCallId(callId);
    if (
      connectionState === ConnectionState.Connected &&
      !joinedTracedRef.current
    ) {
      joinedTracedRef.current = true;
      callTraceJournal.markConnected();
      callTraceJournal.record({
        eventType: "call.join_succeeded",
        stage: "call",
        outcome: "succeeded",
        payload: { hasCallId: Boolean(callId) },
      });
      callTraceJournal.flush();
    }
  }, [callId, connectionState]);
  const leaveCall = useCallback(
    (cause: "user", soundFile?: CallEventSounds): void => {
      intentionalLeaveRef.current = true;
      onLeave(cause, soundFile);
    },
    [onLeave],
  );

  const containerRef1 = useRef<HTMLDivElement | null>(null);
  const [containerRef2, bounds] = useMeasure();
  // Merge the refs so they can attach to the same element
  const containerRef = useMergedRefs(containerRef1, containerRef2);

  const { hideScreensharing, showControls } = useUrlParams();

  const { isScreenShareEnabled, localParticipant } = useLocalParticipant({
    room: livekitRoom,
  });

  const muteAllAudio = useBehavior(muteAllAudio$);
  // Call pickup state and display names are needed for waiting overlay/sounds
  const callPickupState = useBehavior(vm.callPickupState$);
  const callProgress = useBehavior(vm.callProgress$);

  // Preload a waiting and decline sounds
  const pickupPhaseSoundCache = useInitial(async () => {
    return prefetchSounds({
      waiting: { mp3: ringtoneMp3, ogg: ringtoneOgg },
    });
  });

  const pickupPhaseAudio = useAudioContext({
    sounds: pickupPhaseSoundCache,
    latencyHint: "interactive",
    muted: muteAllAudio,
  });

  // This seems like it might be enough logic to use move it into the call view model?
  const [didFallbackToRoomKey, setDidFallbackToRoomKey] = useState(false);
  useTypedEventEmitter(
    rtcSession,
    RoomAndToDeviceEvents.EnabledTransportsChanged,
    (enabled) => setDidFallbackToRoomKey(enabled.room),
  );

  const [developerMode] = useSetting(developerModeSetting);
  const [useExperimentalToDeviceTransport] = useSetting(
    useExperimentalToDeviceTransportSetting,
  );
  const encryptionSystem = useRoomEncryptionSystem(matrixRoom.roomId);

  const showToDeviceEncryption = useMemo(
    () =>
      developerMode &&
      useExperimentalToDeviceTransport &&
      encryptionSystem.kind === E2eeType.PER_PARTICIPANT &&
      !didFallbackToRoomKey,
    [
      developerMode,
      useExperimentalToDeviceTransport,
      encryptionSystem.kind,
      didFallbackToRoomKey,
    ],
  );

  const toggleMicrophone = useCallback(() => {
    if (!hardRecoveryActive) muteStates.audio.setEnabled?.((e) => !e);
  }, [hardRecoveryActive, muteStates]);
  const toggleCamera = useCallback(() => {
    if (!hardRecoveryActive) muteStates.video.setEnabled?.((e) => !e);
  }, [hardRecoveryActive, muteStates]);

  // This function incorrectly assumes that there is a camera and microphone, which is not always the case.
  // TODO: Make sure that this module is resilient when it comes to camera/microphone availability!
  useCallViewKeyboardShortcuts(
    containerRef1,
    toggleMicrophone,
    toggleCamera,
    (muted) => {
      if (!hardRecoveryActive) muteStates.audio.setEnabled?.(!muted);
    },
    (reaction) => {
      if (!hardRecoveryActive) void sendReaction(reaction);
    },
    () => {
      if (!hardRecoveryActive) void toggleRaisedHand();
    },
  );

  const windowMode = useBehavior(vm.windowMode$);
  const layout = useBehavior(vm.layout$);
  const tileStoreGeneration = useBehavior(vm.tileStoreGeneration$);
  const [debugTileLayout] = useSetting(debugTileLayoutSetting);
  const gridMode = useBehavior(vm.gridMode$);
  const showHeader = useBehavior(vm.showHeader$);
  const showFooter = useBehavior(vm.showFooter$);
  const earpieceMode = useBehavior(vm.earpieceMode$);
  const audioOutputSwitcher = useBehavior(vm.audioOutputSwitcher$);

  useEffect(() => {
    const reconnectStartedAtRef = { current: 0 };
    const reconnectStartedEventIdRef = { current: null as string | null };
    const onReconnecting = (): void => {
      reconnectStartedAtRef.current = performance.now();
      callTraceJournal.setAttemptState("reconnecting");
      reconnectStartedEventIdRef.current = callTraceJournal.record({
        eventType: "livekit.reconnect_started",
        stage: "livekit",
        severity: "warn",
        outcome: "started",
        payload: getLivekitIncidentSnapshot(livekitRoom, rtcSession),
      });
    };
    const onReconnected = (): void => {
      callTraceJournal.setAttemptState("connected");
      callTraceJournal.record({
        eventType: "livekit.reconnect_succeeded",
        stage: "livekit",
        outcome: "recovered",
        relatedEventId: reconnectStartedEventIdRef.current,
        durationMs:
          reconnectStartedAtRef.current > 0
            ? Math.round(performance.now() - reconnectStartedAtRef.current)
            : null,
        payload: getLivekitIncidentSnapshot(livekitRoom, rtcSession),
      });
      reconnectStartedAtRef.current = 0;
      reconnectStartedEventIdRef.current = null;
    };
    const onDisconnected = (reason?: DisconnectReason): void => {
      const hardRecoveryDisconnect = isHardRecoveryActive();
      const disconnectReason =
        reason === undefined
          ? "unknown"
          : (DisconnectReason[reason] ?? String(reason));
      callTraceJournal.record({
        eventType: "livekit.disconnected",
        stage: "livekit",
        severity:
          intentionalLeaveRef.current || hardRecoveryDisconnect
            ? "info"
            : "error",
        outcome: intentionalLeaveRef.current
          ? "cancelled"
          : hardRecoveryDisconnect
            ? "state_changed"
            : "failed",
        reasonCode: disconnectReason,
        relatedEventId: reconnectStartedEventIdRef.current,
        payload: {
          intentional: intentionalLeaveRef.current,
          ...getLivekitIncidentSnapshot(livekitRoom, rtcSession),
        },
      });
      if (hardRecoveryDisconnect) return;
      callTraceJournal.finishConnection(
        intentionalLeaveRef.current ? "disconnected" : "failed",
        disconnectReason,
      );
      if (!intentionalLeaveRef.current && reconnectStartedAtRef.current > 0) {
        callTraceJournal.record({
          eventType: "livekit.reconnect_failed",
          stage: "livekit",
          severity: "error",
          outcome: "failed",
          reasonCode:
            reason === undefined
              ? "unknown"
              : (DisconnectReason[reason] ?? String(reason)),
          relatedEventId: reconnectStartedEventIdRef.current,
          durationMs: Math.round(
            performance.now() - reconnectStartedAtRef.current,
          ),
          payload: getLivekitIncidentSnapshot(livekitRoom, rtcSession),
        });
      }
    };

    livekitRoom.on(LivekitRoomEvent.Reconnecting, onReconnecting);
    livekitRoom.on(LivekitRoomEvent.Reconnected, onReconnected);
    livekitRoom.on(LivekitRoomEvent.Disconnected, onDisconnected);

    return (): void => {
      livekitRoom.off(LivekitRoomEvent.Reconnecting, onReconnecting);
      livekitRoom.off(LivekitRoomEvent.Reconnected, onReconnected);
      livekitRoom.off(LivekitRoomEvent.Disconnected, onDisconnected);
    };
  }, [isHardRecoveryActive, livekitRoom, rtcSession]);

  useEffect(() => {
    const wasReconnecting = previousReconnectingRef.current;
    previousReconnectingRef.current = reconnecting;

    if (!reconnecting || wasReconnecting) {
      return;
    }
    // Reconnect внутри hard cycle относится к единому cycle incident.
    if (isHardRecoveryActive()) return;

    autoRageshake.recordReconnect({
      screen: "InCallView",
      details: {
        callId: callId || undefined,
        connectionState,
        callPickupState,
        participantCount,
        membershipsCount: memberships.length,
      },
    });
  }, [
    autoRageshake,
    callId,
    callPickupState,
    connectionState,
    memberships,
    participantCount,
    reconnecting,
    isHardRecoveryActive,
  ]);

  useSubscription(vm.autoLeave$, () => {
    callTraceJournal.record({
      eventType: "call.auto_leave_triggered",
      stage: "call",
      outcome: "started",
      reasonCode: "all_others_left",
    });
    leaveCall("user");
  });

  // We need to set the proper timings on the animation based upon the sound length.
  const ringDuration = pickupPhaseAudio?.soundDuration["waiting"] ?? 1;
  useEffect((): (() => void) => {
    // The CSS animation includes the delay, so we must double the length of the sound.
    window.document.body.style.setProperty(
      "--call-ring-duration-s",
      `${ringDuration * 2}s`,
    );
    window.document.body.style.setProperty(
      "--call-ring-delay-s",
      `${ringDuration}s`,
    );
    // Remove properties when we unload.
    return () => {
      window.document.body.style.removeProperty("--call-ring-duration-s");
      window.document.body.style.removeProperty("--call-ring-delay-s");
    };
  }, [pickupPhaseAudio?.soundDuration, ringDuration]);

  // When we enter timeout or decline we will leave the call.
  // Уходим не мгновенно: раньше звонок просто закрывался, и звонящий так и не
  // узнавал, отклонили его или никто не подошёл. Пары секунд хватает прочитать
  // исход, а выход из звонка остаётся прежним.
  useEffect((): void | (() => void) => {
    const soundFile: CallEventSounds | null =
      callPickupState === "timeout"
        ? "timeout"
        : callPickupState === "decline"
          ? "decline"
          : null;
    if (soundFile === null) return;

    const noticeTimeout = window.setTimeout(
      () => leaveCall("user", soundFile),
      CALL_OUTCOME_NOTICE_MS,
    );
    return () => window.clearTimeout(noticeTimeout);
  }, [callPickupState, leaveCall]);

  // When waiting for pickup, loop a waiting sound
  useEffect((): void | (() => void) => {
    if (callPickupState !== "ringing" || !pickupPhaseAudio) return;
    const endSound = pickupPhaseAudio.playSoundLooping("waiting", ringDuration);
    return () => {
      void endSound().catch((e) => {
        logger.error("Failed to stop ringing sound", e);
      });
    };
  }, [callPickupState, pickupPhaseAudio, ringDuration]);

  // Waiting UI overlay
  const waitingOverlay: JSX.Element | null = useMemo(() => {
    // Показываем только то, что подтверждено сигналом. «Принято» не рисуем:
    // собеседник появляется на экране сам, а затемнять только что начавшийся
    // звонок ради надписи — хуже, чем промолчать.
    // null здесь — это и «событие вызова ещё не ушло», и «ожидание ответа
    // выключено»: в обоих случаях сказать нечего.
    if (callProgress === null || callProgress === "accepted") return null;

    // Use room state for other participants data (the one that we likely want to reach)
    const roomOthers = [
      ...matrixRoom.getMembersWithMembership("join"),
      ...matrixRoom.getMembersWithMembership("invite"),
    ].filter((m) => m.userId !== client.getUserId());
    // Yield if there are not other members in the room.
    if (roomOthers.length === 0) return null;

    const otherMember = roomOthers.length > 0 ? roomOthers[0] : undefined;
    const isOneOnOne = roomOthers.length === 1 && otherMember;
    const name = otherMember?.name ?? otherMember?.userId ?? "";
    let text: string;
    switch (callProgress) {
      case "ringing":
        text = isOneOnOne
          ? t("in_call_view.ringing_for_participant", { name })
          : t("in_call_view.ringing_for_other_participants");
        break;
      case "declined":
        text = t("in_call_view.call_declined");
        break;
      case "no_answer":
        text = t("in_call_view.call_not_answered");
        break;
      default:
        // "sent": событие вызова принято сервером, но подтверждения с той
        // стороны ещё нет — обещать «звонит» нельзя.
        text = isOneOnOne
          ? t("in_call_view.waiting_for_participant_to_join", { name })
          : t("in_call_view.waiting_for_other_participants");
        break;
    }
    const stillWaiting = callProgress === "sent" || callProgress === "ringing";
    const avatarMxc = isOneOnOne
      ? (otherMember.getMxcAvatarUrl?.() ?? undefined)
      : (matrixRoom.getMxcAvatarUrl() ?? undefined);

    return (
      <div className={classNames(overlayStyles.bg, waitingStyles.overlay)}>
        <div
          className={classNames(overlayStyles.content, waitingStyles.content)}
        >
          <div className={stillWaiting ? waitingStyles.pulse : undefined}>
            <Avatar
              id={isOneOnOne ? otherMember.userId : matrixRoom.roomId}
              name={isOneOnOne ? otherMember.name : matrixRoom.name}
              src={avatarMxc}
              size={AvatarSize.XL}
            />
          </div>
          <Text size="md" className={waitingStyles.text}>
            {text}
          </Text>
        </div>
      </div>
    );
  }, [callProgress, client, matrixRoom, t]);

  // Ideally we could detect taps by listening for click events and checking
  // that the pointerType of the event is "touch", but this isn't yet supported
  // in Safari: https://developer.mozilla.org/en-US/docs/Web/API/Element/click_event#browser_compatibility
  // Instead we have to watch for sufficiently fast touch events.
  const touchStart = useRef<number | null>(null);
  const onTouchStart = useCallback(() => (touchStart.current = Date.now()), []);
  const onTouchEnd = useCallback(() => {
    const start = touchStart.current;
    if (start !== null && Date.now() - start <= maxTapDurationMs)
      vm.tapScreen();
    touchStart.current = null;
  }, [vm]);
  const onTouchCancel = useCallback(() => (touchStart.current = null), []);

  // We also need to tell the footer controls to prevent touch events from
  // bubbling up, or else the footer will be dismissed before a click/change
  // event can be registered on the control
  const onControlsTouchEnd = useCallback(
    (e: TouchEvent) => {
      // Somehow applying pointer-events: none to the controls when the footer
      // is hidden is not enough to stop clicks from happening as the footer
      // becomes visible, so we check manually whether the footer is shown
      if (showFooter) {
        e.stopPropagation();
        vm.tapControls();
      } else {
        e.preventDefault();
      }
    },
    [vm, showFooter],
  );

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      if (e.pointerType === "mouse") vm.hoverScreen();
    },
    [vm],
  );
  const onPointerOut = useCallback(() => vm.unhoverScreen(), [vm]);

  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState(defaultSettingsTab);

  const openSettings = useCallback(
    () => setSettingsModalOpen(true),
    [setSettingsModalOpen],
  );
  const closeSettings = useCallback(
    () => setSettingsModalOpen(false),
    [setSettingsModalOpen],
  );

  const openProfile = useMemo(
    () =>
      // Profile settings are unavailable in widget mode
      widget === null
        ? (): void => {
            setSettingsTab("profile");
            setSettingsModalOpen(true);
          }
        : null,
    [setSettingsTab, setSettingsModalOpen],
  );

  const [headerRef, headerBounds] = useMeasure();
  const [footerRef, footerBounds] = useMeasure();
  const callElapsedTimerTop =
    headerBounds.height > 0 ? headerBounds.height + 12 : 12;

  const gridBounds = useMemo(
    () => ({
      width: bounds.width,
      height:
        bounds.height -
        headerBounds.height -
        (windowMode === "flat" ? 0 : footerBounds.height),
    }),
    [
      bounds.width,
      bounds.height,
      headerBounds.height,
      footerBounds.height,
      windowMode,
    ],
  );
  const gridBoundsObservable$ = useObservable(
    (inputs$) => inputs$.pipe(map(([gridBounds]) => gridBounds)),
    [gridBounds],
  );

  const spotlightAlignment$ = useInitial(
    () => new BehaviorSubject(defaultSpotlightAlignment),
  );
  const pipAlignment$ = useInitial(
    () => new BehaviorSubject(defaultPipAlignment),
  );

  const setGridMode = useCallback(
    (mode: GridMode) => {
      if (!hardRecoveryActive) vm.setGridMode(mode);
    },
    [hardRecoveryActive, vm],
  );

  useEffect(() => {
    widget?.api.transport
      .send(
        gridMode === "grid"
          ? ElementWidgetActions.TileLayout
          : ElementWidgetActions.SpotlightLayout,
        {},
      )
      .catch((e) => {
        logger.error("Failed to send layout change to widget API", e);
      });
  }, [gridMode]);

  useEffect(() => {
    if (widget) {
      const onTileLayout = (ev: CustomEvent<IWidgetApiRequest>): void => {
        setGridMode("grid");
        widget!.api.transport.reply(ev.detail, {});
      };
      const onSpotlightLayout = (ev: CustomEvent<IWidgetApiRequest>): void => {
        setGridMode("spotlight");
        widget!.api.transport.reply(ev.detail, {});
      };

      widget.lazyActions.on(ElementWidgetActions.TileLayout, onTileLayout);
      widget.lazyActions.on(
        ElementWidgetActions.SpotlightLayout,
        onSpotlightLayout,
      );

      return (): void => {
        widget!.lazyActions.off(ElementWidgetActions.TileLayout, onTileLayout);
        widget!.lazyActions.off(
          ElementWidgetActions.SpotlightLayout,
          onSpotlightLayout,
        );
      };
    }
  }, [setGridMode]);

  useAppBarSecondaryButton(
    useMemo(() => {
      let shareButton = null;
      let audioSwitcherButton = null;

      if (onShareClick) {
        shareButton = <ShareLinkButton variant="icon" onClick={onShareClick} />;
      }

      if (audioOutputSwitcher) {
        const isEarpieceTarget =
          audioOutputSwitcher.targetOutput === "earpiece";
        const Icon = isEarpieceTarget ? VoiceCallSolidIcon : VolumeOnSolidIcon;
        const label = isEarpieceTarget
          ? t("settings.devices.handset")
          : t("settings.devices.loudspeaker");
        audioSwitcherButton = (
          <Tooltip label={label}>
            <IconButton
              disabled={hardRecoveryActive}
              onClick={(e) => {
                e.preventDefault();
                audioOutputSwitcher.switch();
              }}
            >
              <Icon />
            </IconButton>
          </Tooltip>
        );
      }

      return (
        <div className={styles.secondaryButtons}>
          {shareButton}
          {audioSwitcherButton}
        </div>
      );
    }, [t, audioOutputSwitcher, hardRecoveryActive, onShareClick]),
  );

  useAppBarHidden(!showHeader);

  let header: ReactNode = null;
  if (showHeader) {
    switch (headerStyle) {
      case "none":
        // Cosmetic header to fill out space while still affecting the bounds of
        // the grid
        header = <div className={classNames(styles.header, styles.filler)} />;
        break;
      case "standard":
        header = (
          <Header
            className={styles.header}
            disconnectedBanner={false} // This screen has its own 'reconnecting' toast
          >
            <LeftNav>
              <RoomHeaderInfo
                id={matrixInfo.roomId}
                name={matrixInfo.roomName}
                avatarUrl={matrixInfo.roomAvatar}
                encrypted={matrixInfo.e2eeSystem.kind !== E2eeType.NONE}
                participantCount={participantCount}
              />
            </LeftNav>
            <RightNav>
              {showControls && onShareClick !== null && (
                <InviteButton
                  className={styles.invite}
                  onClick={onShareClick}
                  disabled={hardRecoveryActive}
                />
              )}
            </RightNav>
          </Header>
        );
    }
  }

  // The reconnecting toast cannot be dismissed
  const onDismissReconnectingToast = useCallback(() => {}, []);
  // We need to use a non-modal toast to avoid trapping focus within the toast.
  // However, a non-modal toast will not render any background overlay on its
  // own, so we must render one manually.
  const reconnectingToast = (
    <>
      <div
        className={classNames(overlayStyles.bg, overlayStyles.animate)}
        data-state={showReconnectToast ? "open" : "closed"}
      />
      <Toast
        onDismiss={onDismissReconnectingToast}
        open={showReconnectToast}
        modal={false}
      >
        {t("common.reconnecting")}
      </Toast>
    </>
  );
  const screenShareStoppedToast = screenShareNoticeCycleId ? (
    <Toast
      key={screenShareNoticeCycleId}
      open
      onDismiss={() => setScreenShareNoticeCycleId(null)}
      autoDismiss={8000}
      modal={false}
    >
      {t("in_call_view.screen_share_stopped_after_recovery")}
    </Toast>
  ) : null;

  const earpieceOverlay = (
    <EarpieceOverlay
      show={earpieceMode && !reconnecting}
      onBackToVideoPressed={audioOutputSwitcher?.switch}
    />
  );

  // If the reconnecting toast or earpiece overlay obscures the media tiles, we
  // need to remove them from the accessibility tree and block focus.
  const contentObscured = showReconnectToast || earpieceMode;
  const {
    recordingNoticeText,
    recordingNoticeVariant,
    onRecordingNotice,
    onProtocolistNotice,
  } = useRecordingNotice(client, matrixRoom);
  const [protocolistRecipientsModalOpen, setProtocolistRecipientsModalOpen] =
    useState(false);

  const recorder = useCallRecording(livekitRoom, matrixRoom, {
    onFinished: (blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Запись встречи-${new Date().toLocaleString()}.webm`;
      a.click();
      URL.revokeObjectURL(url);
    },
    onRecordingNotice,
  });
  const protocolist = useProtocolistRecording(livekitRoom, matrixRoom, {
    onNotice: onProtocolistNotice,
  });
  const automation = useMeetingAutomation(
    client,
    matrixRoom.roomId,
    recorder,
    protocolist,
    connectionState === ConnectionState.Connected && !!livekitRoom.name,
  );
  const { shouldOpenRecipientsModal, consumeRecipientsModalRequest } =
    protocolist;

  useEffect(() => {
    if (!shouldOpenRecipientsModal) {
      return;
    }

    setProtocolistRecipientsModalOpen(true);
    consumeRecipientsModalRequest();
  }, [consumeRecipientsModalRequest, shouldOpenRecipientsModal]);

  const Tile = useMemo(
    () =>
      function Tile({
        ref,
        className,
        style,
        targetWidth,
        targetHeight,
        model,
      }: TileProps<TileViewModel, HTMLDivElement>): ReactNode {
        const spotlightExpanded = useBehavior(vm.spotlightExpanded$);
        const onToggleExpanded = useBehavior(vm.toggleSpotlightExpanded$);
        const showSpeakingIndicatorsValue = useBehavior(
          vm.showSpeakingIndicators$,
        );
        const showSpotlightIndicatorsValue = useBehavior(
          vm.showSpotlightIndicators$,
        );

        return model instanceof GridTileViewModel ? (
          <GridTile
            ref={ref}
            vm={model}
            onOpenProfile={openProfile}
            isUserLocallyRecording={recorder.isUserLocallyRecording}
            targetWidth={targetWidth}
            targetHeight={targetHeight}
            className={classNames(className, styles.tile)}
            style={style}
            showSpeakingIndicators={showSpeakingIndicatorsValue}
            focusable={!contentObscured}
          />
        ) : (
          <SpotlightTile
            ref={ref}
            vm={model}
            expanded={spotlightExpanded}
            onToggleExpanded={onToggleExpanded}
            targetWidth={targetWidth}
            targetHeight={targetHeight}
            showIndicators={showSpotlightIndicatorsValue}
            isUserLocallyRecording={recorder.isUserLocallyRecording}
            focusable={!contentObscured}
            className={classNames(className, styles.tile)}
            style={style}
          />
        );
      },
    [vm, openProfile, recorder.isUserLocallyRecording, contentObscured],
  );

  const localRecordingUsersSignature = useMemo(
    () => Array.from(recorder.localRecordingUserIds).sort().join("|"),
    [recorder.localRecordingUserIds],
  );

  const layouts = useMemo(() => {
    const inputs = {
      minBounds$: gridBoundsObservable$,
      spotlightAlignment$,
      pipAlignment$,
    };
    return {
      grid: makeGridLayout(inputs),
      "spotlight-landscape": makeSpotlightLandscapeLayout(inputs),
      "spotlight-portrait": makeSpotlightPortraitLayout(inputs),
      "spotlight-expanded": makeSpotlightExpandedLayout(inputs),
      "one-on-one": makeOneOnOneLayout(inputs),
    };
  }, [gridBoundsObservable$, spotlightAlignment$, pipAlignment$]);

  const renderContent = (): JSX.Element => {
    if (layout.type === "pip") {
      return (
        <SpotlightTile
          className={classNames(styles.tile, styles.maximised)}
          vm={layout.spotlight}
          expanded
          onToggleExpanded={null}
          targetWidth={gridBounds.width}
          targetHeight={gridBounds.height}
          showIndicators={false}
          isUserLocallyRecording={recorder.isUserLocallyRecording}
          focusable={!contentObscured}
          aria-hidden={contentObscured}
        />
      );
    }

    const layers = layouts[layout.type] as CallLayoutOutputs<Layout>;
    const fixedGrid = (
      <Grid
        key="fixed"
        className={styles.fixedGrid}
        style={{
          insetBlockStart:
            headerBounds.height > 0 ? headerBounds.bottom : bounds.top,
          height: gridBounds.height,
        }}
        model={layout}
        Layout={layers.fixed}
        Tile={Tile}
        tileRenderNonce={localRecordingUsersSignature}
        aria-hidden={contentObscured}
      />
    );
    const scrollingGrid = (
      <Grid
        key="scrolling"
        className={classNames(styles.scrollingGrid, {
          [styles.spotlightLandscapeGrid]:
            layout.type === "spotlight-landscape",
        })}
        style={
          layout.type === "spotlight-landscape"
            ? { height: gridBounds.height }
            : undefined
        }
        model={layout}
        Layout={layers.scrolling}
        Tile={Tile}
        tileRenderNonce={localRecordingUsersSignature}
        aria-hidden={contentObscured}
      />
    );
    // The grid tiles go *under* the spotlight in the portrait layout, but
    // *over* the spotlight in the expanded layout
    return layout.type === "spotlight-expanded" ? (
      <>
        {fixedGrid}
        {scrollingGrid}
      </>
    ) : (
      <>
        {scrollingGrid}
        {fixedGrid}
      </>
    );
  };

  const rageshakeRequestModalProps = useRageshakeRequestModal(
    matrixRoom.roomId,
  );

  const toggleScreensharing = useCallback(() => {
    if (hardRecoveryActive) return;
    localParticipant
      .setScreenShareEnabled(
        !isScreenShareEnabled,
        {
          audio: true,
          selfBrowserSurface: "include",
          surfaceSwitching: "include",
          systemAudio: "include",
        },
        SCREEN_SHARE_PUBLISH_OPTIONS,
      )
      .catch(logger.error);
  }, [hardRecoveryActive, localParticipant, isScreenShareEnabled]);

  const buttons: JSX.Element[] = [];

  buttons.push(
    <MicButton
      key="audio"
      shortcut="M"
      muted={!muteStates.audio.enabled}
      onClick={toggleMicrophone}
      onTouchEnd={onControlsTouchEnd}
      disabled={hardRecoveryActive || muteStates.audio.setEnabled === null}
      data-testid="incall_mute"
    />,
    <VideoButton
      key="video"
      shortcut="V"
      muted={!muteStates.video.enabled}
      onClick={toggleCamera}
      onTouchEnd={onControlsTouchEnd}
      disabled={hardRecoveryActive || muteStates.video.setEnabled === null}
      data-testid="incall_videomute"
    />,
  );
  if (supportsReactions) {
    buttons.push(
      <RaiseHandButton
        vm={vm}
        key="raise_hand"
        className={styles.raiseHand}
        identifier={`${client.getUserId()}:${client.getDeviceId()}`}
        onTouchEnd={onControlsTouchEnd}
        disabled={hardRecoveryActive}
      />,
    );
  }
  if (canScreenshare && !hideScreensharing) {
    buttons.push(
      <ShareScreenButton
        key="share_screen"
        className={styles.shareScreen}
        enabled={isScreenShareEnabled}
        onClick={toggleScreensharing}
        onTouchEnd={onControlsTouchEnd}
        data-testid="incall_screenshare"
        disabled={hardRecoveryActive}
      />,
    );
  }
  if (supportsReactions) {
    buttons.push(
      <ReactionToggleButton
        vm={vm}
        key="reactions"
        identifier={`${client.getUserId()}:${client.getDeviceId()}`}
        onTouchEnd={onControlsTouchEnd}
        disabled={hardRecoveryActive}
      />,
    );
  }
  buttons.push(
    <MoreOptionsMenuButton
      automationControls={automation.controls}
      key="recording"
      className={styles.recordCallButton}
      recorder={recorder}
      protocolist={protocolist}
      canScreenshare={canScreenshare}
      onShareClick={onShareClick}
      onSettingsClick={layout.type !== "pip" ? openSettings : null}
      onTouchEnd={onControlsTouchEnd}
      disabled={hardRecoveryActive}
    />,
  );

  buttons.push(
    <EndCallButton
      key="end_call"
      onClick={function (): void {
        leaveCall("user");
      }}
      onTouchEnd={onControlsTouchEnd}
      data-testid="incall_leave"
    />,
  );
  const footer = (
    <div
      ref={footerRef}
      className={classNames(styles.footer, {
        [styles.overlay]: windowMode === "flat",
        [styles.hidden]:
          !showFooter || (!showControls && headerStyle === "none"),
      })}
    >
      {headerStyle !== "none" && (
        <div className={styles.logo}>
          <LogoMark width={24} height={24} aria-hidden />
          <span className={styles.brandName} aria-label={PRODUCT_NAME}>
            {PRODUCT_NAME}
          </span>
          {/* Don't mind this odd placement, it's just a little debug label */}
          {debugTileLayout
            ? `Tiles generation: ${tileStoreGeneration}`
            : undefined}
        </div>
      )}
      {showControls && <div className={styles.buttons}>{buttons}</div>}
      {showControls && (
        <LayoutToggle
          className={styles.layout}
          layout={gridMode}
          setLayout={setGridMode}
          onTouchEnd={onControlsTouchEnd}
          disabled={hardRecoveryActive}
        />
      )}
    </div>
  );

  return (
    <AgendaProvider>
      <div
        className={styles.inRoom}
        data-layout={layout.type}
        data-window-mode={windowMode}
        ref={containerRef}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchCancel}
        onPointerMove={onPointerMove}
        onPointerOut={onPointerOut}
      >
        {/* Agenda is call content, not header chrome. Desktop widgets use
          header="none" and flat layouts hide the header altogether. Measure
          both rows together so video tiles and overlays stay below the agenda. */}
        <div
          className={styles.topBar}
          ref={headerRef}
          data-testid="call-top-bar"
        >
          {header}
          {layout.type !== "pip" && <AgendaStrip />}
        </div>
        {
          // TODO: remove this once we remove the developer flag gets removed and we have shipped to
          // device transport as the default.
          showToDeviceEncryption && (
            <Text
              style={{ height: 0, zIndex: 1, alignSelf: "center", margin: 0 }}
              size="sm"
            >
              using to Device key transport
            </Text>
          )
        }
        {isCallElapsedTimerVisible && (
          <div
            className={styles.callElapsedTimer}
            style={{ top: `${callElapsedTimerTop}px` }}
          >
            {callElapsedTimer}
          </div>
        )}
        {recordingNoticeText && (
          <div
            className={classNames(styles.recordingNotice, {
              [styles.recordingNoticeStart]: recordingNoticeVariant === "start",
              [styles.recordingNoticeStop]: recordingNoticeVariant === "stop",
            })}
            style={{
              top: `${isCallElapsedTimerVisible ? callElapsedTimerTop + 36 : callElapsedTimerTop}px`,
            }}
          >
            {recordingNoticeText}
          </div>
        )}
        {automation.error && (
          <div
            role="alert"
            className={styles.automationError}
            style={{ top: callElapsedTimerTop + 36 }}
          >
            <span>{automation.error}</span>
            <CpdButton kind="secondary" size="sm" onClick={automation.retry}>
              Повторить автозапуск
            </CpdButton>
          </div>
        )}
        <MatrixAudioRenderer members={memberships} muted={muteAllAudio} />
        <MeetingModeration
          room={livekitRoom}
          matrixRoom={matrixRoom}
          mute={() => muteStates.audio.setEnabled?.(false)}
          showControls={showControls && layout.type !== "pip"}
        />
        {renderContent()}
        <CallEventAudioRenderer vm={vm} muted={muteAllAudio} />
        <ReactionsAudioRenderer vm={vm} muted={muteAllAudio} />
        {reconnectingToast}
        {screenShareStoppedToast}
        {earpieceOverlay}
        <ReactionsOverlay vm={vm} />
        {
          // В pip-режиме окно размером с плитку — списку там просто негде уместиться
          layout.type !== "pip" && (
            <RaisedHandsOverlay
              vm={vm}
              style={{ insetBlockStart: `${callElapsedTimerTop}px` }}
            />
          )
        }
        {waitingOverlay}
        {footer}
        {layout.type !== "pip" && (
          <>
            <RageshakeRequestModal {...rageshakeRequestModalProps} />
            <SettingsModal
              client={client}
              roomId={matrixRoom.roomId}
              open={settingsModalOpen}
              onDismiss={closeSettings}
              tab={settingsTab}
              onTabChange={setSettingsTab}
              livekitRoom={livekitRoom}
            />
            <ProtocolistRecipientsModal
              open={protocolistRecipientsModalOpen}
              onDismiss={() => setProtocolistRecipientsModalOpen(false)}
              protocolist={protocolist}
            />
          </>
        )}
      </div>
    </AgendaProvider>
  );
};
